import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card } from "../../components/ui/card";
import { ArrowLeft, Save, Upload, Image as ImageIcon, X, FileJson } from "lucide-react";
import { toast } from "sonner";
import MarkdownEditor from "../../components/MarkdownEditor";
import ExamQuestionsEditor, { newQuestion } from "../../components/admin/ExamQuestionsEditor";
import TheoryFilesUploader from "../../components/admin/TheoryFilesUploader";
import { questionsFromCsvText, questionsFromJsonText } from "../../lib/examImport";

/** Taller = curso con un único examen implícito: no hay gestión de "temas"
 * (añadir/reordenar/eliminar) — solo los campos del curso más los del
 * examen único, en una sola pantalla. Reutiliza el mismo recurso backend
 * `/admin/courses` (kind="normal") que AdminTestEditor.jsx usa para Cursos. */
export default function AdminWorkshopEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const canEditPrice =
    currentUser?.role === "superadmin" ||
    currentUser?.role === "admin" ||
    !!(currentUser?.perms || {}).edit_course_price;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [studyHours, setStudyHours] = useState("");
  const [price, setPrice] = useState("");
  const [moreInfo, setMoreInfo] = useState("");
  const [area, setArea] = useState("");
  const [existingAreas, setExistingAreas] = useState([]);

  const [examId, setExamId] = useState(null);
  const [passPercentage, setPassPercentage] = useState(70);
  const [timeLimitMinutes, setTimeLimitMinutes] = useState(0);
  const [questions, setQuestions] = useState([newQuestion()]);
  const [theoryFiles, setTheoryFiles] = useState([]);
  const [examBusy, setExamBusy] = useState(false);

  const [saving, setSaving] = useState(false);
  const [headerImage, setHeaderImage] = useState(null);
  const [headerBusy, setHeaderBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/courses", { params: { kind: "normal" } }).then(({ data }) => {
      const set = new Set();
      (data || []).forEach((c) => {
        if (c.area && c.area.trim()) set.add(c.area.trim());
      });
      setExistingAreas([...set].sort());
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get(`/admin/courses/${id}`).then(({ data }) => {
      setTitle(data.title);
      setDescription(data.description || "");
      setStudyHours(data.study_hours ?? "");
      setPrice(data.price ?? "");
      setMoreInfo(data.more_info || "");
      setArea(data.area || "");
      setHeaderImage(data.header_image || null);
      const exam = (data.exams || [])[0];
      if (exam) {
        setExamId(exam.exam_id);
        setPassPercentage(exam.pass_percentage ?? 70);
        setTimeLimitMinutes(exam.time_limit_minutes || 0);
        setQuestions(
          (exam.questions || []).map((q) => ({
            text: q.text, type: q.type, options: q.options, correct: q.correct || [], block: q.block || "",
          }))
        );
        setTheoryFiles(exam.theory_files || []);
      }
    });
  }, [id]);

  const examPayload = () => ({
    title: title.trim() || "Taller",
    description: description.trim() || title.trim() || "Taller",
    pass_percentage: Number(passPercentage) || 0,
    time_limit_minutes: Number(timeLimitMinutes) || 0,
    order: 1,
    questions,
  });

  const validate = () => {
    if (!title.trim()) {
      toast.error("El título del taller es obligatorio");
      return false;
    }
    for (let qi = 0; qi < questions.length; qi++) {
      const q = questions[qi];
      if (!q.text.trim()) {
        toast.error(`Pregunta ${qi + 1}: falta el texto`);
        return false;
      }
      if (q.options.some((o) => !o.trim())) {
        toast.error(`Pregunta ${qi + 1}: opciones vacías`);
        return false;
      }
      if (q.correct.length === 0) {
        toast.error(`Pregunta ${qi + 1}: marca al menos una respuesta correcta`);
        return false;
      }
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const courseMeta = {
        title,
        description,
        kind: "normal", // Taller → siempre certificado; los Cursos se crean desde /admin/courses
        study_hours: studyHours === "" ? null : Number(studyHours),
        price: price === "" ? null : Number(price),
        more_info: moreInfo,
        area: area.trim() || null,
      };
      if (!id) {
        await api.post("/admin/courses", { ...courseMeta, exams: [examPayload()] });
        toast.success("Guardado");
        navigate("/admin/workshops");
        return;
      }
      await api.put(`/admin/courses/${id}`, courseMeta);
      if (examId) {
        await api.put(`/admin/courses/${id}/exams/${examId}`, examPayload());
      } else {
        const { data } = await api.post(`/admin/courses/${id}/exams`, examPayload());
        const created = data.exams[data.exams.length - 1];
        setExamId(created.exam_id);
      }
      toast.success("Guardado");
      navigate("/admin/workshops");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  // ---- Preguntas: import JSON/CSV ----
  const importQuestions = async (file) => {
    if (!file) return;
    if (!examId) {
      const hasContent = questions.some((q) => q.text.trim());
      if (hasContent && !window.confirm("Esto reemplaza las preguntas actuales. ¿Continuar?")) return;
      try {
        const text = await file.text();
        const fname = file.name.toLowerCase();
        let parsed;
        if (fname.endsWith(".json")) {
          parsed = questionsFromJsonText(text);
        } else if (fname.endsWith(".csv")) {
          parsed = questionsFromCsvText(text);
        } else {
          toast.error("Formato no soportado (usa JSON o CSV)");
          return;
        }
        if (!parsed.length) {
          toast.error("No se encontraron preguntas en el archivo");
          return;
        }
        setQuestions(parsed);
        toast.success("Preguntas importadas. Revísalas y guarda el taller.");
      } catch (err) {
        toast.error(`Archivo inválido: ${err.message}`);
      }
      return;
    }
    if (!window.confirm("Esto reemplaza todas las preguntas actuales. ¿Continuar?")) return;
    setExamBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/admin/courses/${id}/exams/${examId}/import`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const serverExam = data.exams.find((e) => e.exam_id === examId);
      setQuestions(
        serverExam.questions.map((q) => ({
          text: q.text, type: q.type, options: q.options, correct: q.correct || [], block: q.block || "",
        }))
      );
      toast.success("Preguntas importadas");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo importar");
    } finally {
      setExamBusy(false);
    }
  };

  // ---- Documentación (PDF, 0..N) ----
  const refreshTheoryFiles = async () => {
    const { data } = await api.get(`/admin/courses/${id}`);
    const serverExam = (data.exams || []).find((e) => e.exam_id === examId);
    setTheoryFiles(serverExam?.theory_files || []);
  };

  const uploadTheory = async (file, replaceFileId = null) => {
    if (!examId) {
      toast.error("Guarda el taller antes de añadir documentación");
      return;
    }
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Solo PDF");
      return;
    }
    setExamBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = replaceFileId
        ? `/admin/courses/${id}/exams/${examId}/theory/${replaceFileId}`
        : `/admin/courses/${id}/exams/${examId}/theory`;
      const method = replaceFileId ? "put" : "post";
      await api[method](url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshTheoryFiles();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al subir PDF");
    } finally {
      setExamBusy(false);
    }
  };

  const deleteTheory = async (fileId) => {
    if (!window.confirm("¿Eliminar este PDF? Los estudiantes ya no podrán descargarlo.")) return;
    setExamBusy(true);
    try {
      await api.delete(`/admin/courses/${id}/exams/${examId}/theory/${fileId}`);
      toast.success("PDF eliminado");
      await refreshTheoryFiles();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setExamBusy(false);
    }
  };

  const downloadTheory = async (fileId, name) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/admin/courses/${id}/exams/${examId}/theory/${fileId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No se pudo descargar");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name || "material.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  // ---- Header image ----
  const headerPreviewUrl = headerImage
    ? `${API}/public/courses/${id}/header?v=${headerImage.version || headerImage.uploaded_at || ""}`
    : null;

  const uploadHeader = async (file) => {
    if (!id) {
      toast.error("Guarda el taller antes de añadir imagen");
      return;
    }
    if (!file) return;
    const okTypes = ["image/png", "image/jpeg", "image/webp"];
    if (!okTypes.includes(file.type)) {
      toast.error("Formato no soportado. Usa PNG, JPG o WEBP.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagen máx. 5 MB");
      return;
    }
    setHeaderBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/courses/${id}/header`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setHeaderImage(data);
      toast.success("Imagen guardada");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al subir imagen");
    } finally {
      setHeaderBusy(false);
    }
  };

  const deleteHeader = async () => {
    if (!window.confirm("¿Eliminar la imagen de cabecera?")) return;
    setHeaderBusy(true);
    try {
      await api.delete(`/admin/courses/${id}/header`);
      setHeaderImage(null);
      toast.success("Imagen eliminada");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setHeaderBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="workshop-editor-page">
      <Button variant="ghost" onClick={() => navigate("/admin/workshops")} className="text-gray-600">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Volver
      </Button>

      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl text-gray-900">
          {id ? "Editar taller" : "Nuevo taller"}
        </h1>
        <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid="save-workshop">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </div>

      <Card className="p-6 border border-gray-200 shadow-none rounded-lg space-y-4">
        <div>
          <Label htmlFor="title">Título del taller</Label>
          <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="workshop-title-input" />
        </div>
        <div>
          <Label htmlFor="desc">Descripción general</Label>
          <Textarea id="desc" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="area">Área formativa</Label>
          <Input
            id="area"
            value={area}
            onChange={(e) => setArea(e.target.value)}
            placeholder="p. ej. Sanidad, Restauración, Servicios Sociales…"
            list="workshop-area-suggestions"
            data-testid="workshop-area-input"
          />
          <datalist id="workshop-area-suggestions">
            {(existingAreas || []).map((a) => (
              <option key={a} value={a} />
            ))}
          </datalist>
          <p className="text-xs text-gray-500 mt-1">
            En el catálogo público los talleres se agrupan por área. Deja en blanco para mostrar en &quot;Otros&quot;.
          </p>
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="w-44">
            <Label htmlFor="sh">Horas lectivas</Label>
            <Input
              id="sh"
              type="number"
              min={0}
              step="0.5"
              value={studyHours}
              onChange={(e) => setStudyHours(e.target.value)}
              placeholder="Ej: 8"
              data-testid="workshop-study-hours-input"
            />
          </div>
          <div className="w-44">
            <Label htmlFor="price">Precio (€)</Label>
            <Input
              id="price"
              type="number"
              min={0}
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="Ej: 29,90"
              disabled={!canEditPrice}
              data-testid="workshop-price-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              {canEditPrice
                ? "Opcional. Vacío = sin precio."
                : "Solo superadmin o un gestor con el permiso «Editar precio» puede cambiarlo."}
            </p>
          </div>
          <div className="w-32">
            <Label className="text-xs">% para aprobar</Label>
            <Input
              type="number"
              min={0}
              max={100}
              value={passPercentage}
              onChange={(e) => setPassPercentage(e.target.value)}
              data-testid="workshop-pass-percentage-input"
            />
          </div>
          <div className="w-36">
            <Label className="text-xs">Tiempo límite (min)</Label>
            <Input
              type="number"
              min={0}
              value={timeLimitMinutes}
              onChange={(e) => setTimeLimitMinutes(e.target.value)}
              placeholder="0 = sin límite"
              data-testid="workshop-time-limit-input"
            />
          </div>
        </div>
      </Card>

      {/* "Más info" block (shown in the home modal) */}
      <Card className="p-6 border border-gray-200 shadow-none rounded-lg space-y-3">
        <div>
          <Label className="text-sm">Más info (catálogo público)</Label>
          <p className="text-xs text-gray-500 mt-1">
            Este texto se muestra en el botón &quot;Más info&quot; del catálogo de la home pública.
          </p>
        </div>
        <MarkdownEditor
          value={moreInfo}
          onChange={setMoreInfo}
          rows={10}
          placeholder="Describe el taller: a quién va dirigido, contenido, requisitos, qué incluye…"
          testId="workshop-more-info-editor"
        />
      </Card>

      {/* Header image */}
      <Card className="p-6 border border-gray-200 shadow-none rounded-lg space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-brand" />
              <h2 className="font-display text-lg text-gray-900">Imagen de cabecera</h2>
            </div>
            <p className="text-xs text-gray-500 mt-1 max-w-md">
              Proporción 16:9 recomendada, máx. 5 MB, PNG/JPG/WEBP.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="workshop-header-upload"
              type="file"
              accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadHeader(f);
                e.target.value = "";
              }}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={!id || headerBusy}
              onClick={() => document.getElementById("workshop-header-upload").click()}
              data-testid="upload-workshop-header-image"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              {headerImage ? "Reemplazar" : "Subir imagen"}
            </Button>
            {headerImage && (
              <Button variant="ghost" size="sm" disabled={headerBusy} onClick={deleteHeader} title="Eliminar imagen">
                <X className="w-3.5 h-3.5 text-red-500" />
              </Button>
            )}
          </div>
        </div>

        {!id ? (
          <p className="text-xs text-gray-500 italic">Guarda el taller primero para poder añadir una imagen.</p>
        ) : headerImage ? (
          <div className="aspect-[16/9] w-full max-w-md rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
            <img src={headerPreviewUrl} alt={title || "Cabecera del taller"} className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="aspect-[16/9] w-full max-w-md rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center text-gray-400 bg-gray-50">
            <div className="text-center px-4">
              <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-xs">Sin imagen. Sube una para destacar el taller en el catálogo.</p>
            </div>
          </div>
        )}
      </Card>

      {/* Documentación de estudio del taller */}
      <TheoryFilesUploader
        files={theoryFiles}
        canUpload={!!examId}
        busy={examBusy}
        onUpload={uploadTheory}
        onDelete={deleteTheory}
        onDownload={downloadTheory}
        idPrefix="workshop"
        disabledHint="Guarda el taller primero para gestionar la documentación."
      />

      {/* Preguntas */}
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl text-gray-900">Preguntas</h2>
        <div>
          <input
            id="workshop-questions-import"
            type="file"
            accept=".json,.csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) importQuestions(f);
              e.target.value = "";
            }}
          />
          <Button
            variant="outline"
            size="sm"
            disabled={examBusy}
            onClick={() => document.getElementById("workshop-questions-import").click()}
            data-testid="import-workshop-questions"
          >
            <FileJson className="w-4 h-4 mr-2" /> {examId ? "Reimportar preguntas (JSON/CSV)" : "Importar preguntas (JSON/CSV)"}
          </Button>
        </div>
      </div>

      <div className="text-xs text-gray-500 leading-relaxed bg-white border border-gray-200 rounded-lg p-4">
        <p className="font-medium text-gray-700 mb-1">Formato de importación</p>
        <p>
          <span className="font-mono-data">JSON</span>:{" "}
          <code className="font-mono-data">{`{ questions: [{ text, type: "single"|"multiple"|"truefalse", options: ["..."], correct: [0,1], block: "opcional" }] }`}</code>
        </p>
        <p className="mt-1">
          <span className="font-mono-data">CSV</span> columnas:{" "}
          <code className="font-mono-data">question, type, options (separadas por |), correct (índices separados por coma), block (opcional)</code>
        </p>
      </div>

      <Card className="p-6 border-2 border-brand/20 shadow-none rounded-lg">
        <ExamQuestionsEditor questions={questions} onChange={setQuestions} idPrefix="workshop" />
      </Card>
    </div>
  );
}
